import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import {
  flInsert,
  IconColor,
  IconName,
  IconSize,
  provideFlIcons,
} from '@freelancer/ui/icon';
import { SpinnerColor, SpinnerSize } from '@freelancer/ui/spinner';
import { FontWeight } from '@freelancer/ui/text';

@Component({
  standalone: false,
  selector: 'fl-ai-modal',
  template: `
    <div class="Container">
      <div class="Header mb-mid">
        <div class="Header-subcontainer">
          <fl-icon
            class="mr-xxsmall"
            [clickable]="true"
            [name]="'ui-chevron-left'"
            [useIconFont]="true"
            [fillIconFont]="true"
            [size]="IconSize.MID"
            (click)="triggerGoBack()"
          ></fl-icon>
          <p class="font-medium">
            @if (!subFunctionality) {
              {{ functionality }}
            }
            @if (subFunctionality) {
              {{ functionality }}: {{ subFunctionality }}
            }
          </p>
        </div>
        <fl-icon
          [name]="'ui-close'"
          [clickable]="true"
          [clickAnimation]="'none'"
          [hoverAnimation]="'spin'"
          [size]="IconSize.MID"
          (click)="triggerCancel()"
          [useIconFont]="true"
        ></fl-icon>
      </div>
      <div class="TextareaContainer mb-mid">
        <fl-textarea
          class="Textarea"
          [disabled]="isGenerating"
          [control]="control"
          [autoGrow]="true"
          [autoGrowMaxHeight]="152"
          [rows]="1"
        >
        </fl-textarea>
        @if (isRecording) {
          <fl-icon
            class="Spinner mr-xxsmall"
            [name]="'ui-stop-circle'"
            [color]="IconColor.ERROR"
            [clickable]="true"
            [useIconFont]="true"
            [size]="IconSize.MID"
            (click)="triggerStopRecording()"
          ></fl-icon>
        }
        @if (isGenerating && !isRecording) {
          <fl-spinner
            class="Spinner"
            [size]="SpinnerSize.SMALL"
            [color]="SpinnerColor.PINK"
          >
          </fl-spinner>
        }
      </div>
      @if (errorState) {
        <div class="ErrorContainer mb-small">
          <fl-icon
            class="ButtonIcon mr-xxsmall"
            [name]="'ui-error'"
            [color]="IconColor.ERROR"
            [useIconFont]="true"
            [size]="IconSize.MID"
          ></fl-icon>
          <p
            class="text-error"
            i18n="Error state"
          >
            Unable to complete the generation, please try again later.
          </p>
        </div>
        <fl-button
          i18n="Regenerate button"
          [color]="ButtonColor.DEFAULT"
          [size]="ButtonSize.SMALL"
          (click)="triggerRegenerateAction()"
        >
          <fl-icon
            class="ButtonIcon mr-xxxsmall"
            [name]="'ui-refresh'"
            [color]="IconColor.INHERIT"
            [size]="IconSize.SMALL"
            [useIconFont]="true"
            [fillIconFont]="true"
            [weight]="FontWeight.BOLD"
          ></fl-icon>
          Regenerate
        </fl-button>
      } @else {
        @if (showCTAOptions) {
          <div class="ButtonsContainer">
            <fl-button
              class="mr-xxsmall"
              [color]="ButtonColor.DEFAULT"
              [size]="ButtonSize.SMALL"
              (click)="triggerLeftButton()"
            >
              <fl-icon
                class="ButtonIcon mr-xxxsmall"
                [name]="'flInsert'"
                [color]="IconColor.INHERIT"
                [size]="IconSize.SMALL"
              ></fl-icon>
              {{ leftButtonCopy }}
            </fl-button>
            <fl-button
              [color]="ButtonColor.DEFAULT"
              [size]="ButtonSize.SMALL"
              (click)="triggerRightButton()"
            >
              @if (speechToTextGenerating) {
                <fl-spinner
                  [size]="SpinnerSize.SMALL"
                  [color]="SpinnerColor.PINK"
                >
                </fl-spinner>
              }
              @if (!speechToTextGenerating) {
                <fl-icon
                  class="ButtonIcon mr-xxxsmall"
                  [name]="rightButtonIcon"
                  [color]="IconColor.INHERIT"
                  [useIconFont]="true"
                  [size]="IconSize.SMALL"
                ></fl-icon>
              }
              {{ rightButtonCopy }}
            </fl-button>
          </div>
        }
      }
    </div>
  `,
  styleUrls: ['./ai-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideFlIcons({ flInsert })],
})
export class AiModalComponent {
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  IconColor = IconColor;
  IconSize = IconSize;
  FontWeight = FontWeight;
  SpinnerColor = SpinnerColor;
  SpinnerSize = SpinnerSize;

  @Input() functionality?: string;
  @Input() subFunctionality?: string;

  @Input() alternativeAction = true;
  @Input() primaryIcon: string;
  @Input() secondaryIcon: string;
  @Input() isRecording = false;
  @Input() showCTAOptions = false;

  // By default we will use this copy and icons since this would be the most common pattern
  @Input() leftButtonCopy = $localize`Insert after`;
  @Input() rightButtonCopy = $localize`Replace selection`;
  @Input() rightButtonIcon: IconName = 'ui-tick';
  @Input() control: FormControl<string>;
  @Input() isGenerating = false;
  @Input() errorState = false;
  @Input() speechToTextGenerating = false;

  @Output() clickRegenerate = new EventEmitter<void>();
  @Output() clickGoBack = new EventEmitter<void>();
  @Output() clickLeftButton = new EventEmitter<void>();
  @Output() clickRightButton = new EventEmitter<void>();
  @Output() clickDiscard = new EventEmitter<void>();
  @Output() clickCancel = new EventEmitter<void>();
  @Output() clickTriggerModal = new EventEmitter<void>();
  @Output() clickIcon = new EventEmitter<void>();
  @Output() clickStopRecording = new EventEmitter<void>();

  showPrimaryIcon = true;

  triggerGoBack(): void {
    this.clickGoBack.emit();
  }

  triggerLeftButton(): void {
    this.clickLeftButton.emit();
  }

  triggerRightButton(): void {
    this.clickRightButton.emit();
  }

  triggerRegenerateAction(): void {
    this.clickRegenerate.emit();
  }

  triggerCancel(): void {
    this.clickCancel.emit();
  }

  triggerDiscard(): void {
    this.clickDiscard.emit();
  }

  toggleAlternativeActionIcon(): void {
    this.showPrimaryIcon = !this.showPrimaryIcon;
    this.clickIcon.emit();
  }

  triggerStopRecording(): void {
    this.clickStopRecording.emit();
  }
}
